import { useState, useEffect } from 'react';
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { useCatchResponse } from "../../../utils/useCatchResponse";
import CommonConfig from "../../../utils/constant";
import Loader from '../../Shared/Loader/Loader';
import api from '../../../utils/apiClient';
import APIConstant from '../../../utils/pathConstants';
import { useLocation } from 'react-router-dom';

const ParentResultView = (props) => {
    const { catchResponse } = useCatchResponse();
    const location = useLocation();

    const [securityUserID, setSecurityUserID] = useState("");
    const [examResults, setExamResults] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        getFinancialYearData(loginData);
    }, []);

    useEffect(() => {
        const examName = location?.state?.examName;

        if (examName && examResults.length > 0) {
            const index = examResults.findIndex(
                (exam) => exam?.examName?.toLowerCase() === examName?.toLowerCase()
            );

            if (index !== -1) {
                setActiveIndex(index);
            }
        }
    }, [examResults, location]);

    const getFinancialYearData = async (userData) => {
        // Student: use their own id + the sidebar-selected academic year (the
        // `selectedAcademicYear` cookie, which the sidebar year picker keeps in
        // sync); fall back to the current year by date only if it isn't set.
        if (userData?.UserRole === 'Student') {
            const sid = userData?.CreatedBy;
            let fy = CommonConfig.getCookie('selectedAcademicYear') || '';
            if (!fy) {
                const years = await CommonConfig.getFinancialYears(sid) || [];
                const now = new Date();
                const cur = years.find(y => y?.StartDate && y?.EndDate && new Date(y.StartDate) <= now && now <= new Date(y.EndDate));
                fy = cur?._id || years[0]?._id || '';
            }
            GetStudentResultData(userData?._id, fy, sid);
            return;
        }
        const childId = userData?.SelectedChildData?._id;
        const selectedaYear = userData?.SelectedAcademicYearList?.[childId];
        if (childId) {
            GetStudentResultData(childId, selectedaYear, userData?.CreatedBy);
        }
    }

    const GetStudentResultData = async (id, financialId, userId) => {
        try {
            let data = {
                StudentId: id,
                FinancialYearId: financialId,
                UserID: userId
            }
            Loader.show();
            await api.post(APIConstant.path.GetStudentResultDataByParent, data).then(async response => {
                let res = response;
                if (res.success) {
                    setExamResults(res?.data);
                }
                Loader.hide();
            }).catch(err => {
                Loader.hide();
                console.log(err);
                catchResponse(err);
            });
        } catch (err) {
            console.log(err);
            catchResponse(err);
        }
    }

    const markTemplate = (row) => (
        <Tag
            value={row.marks}
            severity={
                row.marks >= 85
                    ? "success"
                    : row.marks >= 60
                        ? "warning"
                        : "danger"
            }
        />
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-check-square"></i>
                    <h2>Result View</h2>
                </div>
            </div>
            <Accordion className='p-mt-4' activeIndex={activeIndex}
                onTabChange={(e) => setActiveIndex(e.index)}>
                {examResults.map((exam, index) => (
                    <AccordionTab key={index} header={exam?.examName}>
                        <Card className="mb-3">
                            <DataTable
                                value={exam?.subjects}
                                size="small"
                                stripedRows
                            >
                                <Column field="subject" header="Subject" />
                                <Column field="marks" header="Marks" body={markTemplate} />
                                <Column field="min" header="Min Marks" />
                                <Column field="max" header="Max Marks" />
                                <Column field="average" header="Avg Marks" />
                            </DataTable>
                        </Card>
                        <div className='p-mt-3 p-mb-2'>
                            <p style={{
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                color: '#1c515e',
                                marginBottom: '1rem',
                                paddingBottom: '0.25rem',
                                borderBottom: '2px solid #14452f'
                            }}>
                                Section Performance
                            </p>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem'
                            }}>
                                <div style={{
                                    background: '#f0fdf4',
                                    borderLeft: '4px solid #16a34a',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(22, 163, 74, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Min Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#16a34a'
                                    }}>
                                        {exam.sectionStats.min}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#fef2f2',
                                    borderLeft: '4px solid #dc2626',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Max Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#dc2626'
                                    }}>
                                        {exam.sectionStats.max}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#fffbeb',
                                    borderLeft: '4px solid #d97706',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(217, 119, 6, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Avg Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#d97706'
                                    }}>
                                        {exam.sectionStats.average}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#f0f4ff',
                                    borderLeft: '4px solid #5b6ddf',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(91, 109, 223, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Rank
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#5b6ddf'
                                    }}>
                                        #{exam.sectionStats.rank}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <p style={{
                                fontSize: '0.95rem',
                                fontWeight: '600',
                                color: '#1c515e',
                                marginBottom: '1rem',
                                paddingBottom: '0.25rem',
                                borderBottom: '2px solid #14452f'
                            }}>
                                Class Performance
                            </p>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '1rem'
                            }}>
                                <div style={{
                                    background: '#f0fdf4',
                                    borderLeft: '4px solid #16a34a',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(22, 163, 74, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Min Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#16a34a'
                                    }}>
                                        {exam.classStats.min}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#fef2f2',
                                    borderLeft: '4px solid #dc2626',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(220, 38, 38, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Max Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#dc2626'
                                    }}>
                                        {exam.classStats.max}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#fffbeb',
                                    borderLeft: '4px solid #d97706',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(217, 119, 6, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Avg Marks
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#d97706'
                                    }}>
                                        {exam.classStats.average}
                                    </div>
                                </div>

                                <div style={{
                                    background: '#f0f4ff',
                                    borderLeft: '4px solid #5b6ddf',
                                    borderRadius: '0.5rem',
                                    padding: '1.25rem',
                                    boxShadow: '0 1px 3px rgba(91, 109, 223, 0.08)',
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#64748b',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Rank
                                    </div>
                                    <div style={{
                                        fontSize: '2rem',
                                        fontWeight: '700',
                                        color: '#5b6ddf'
                                    }}>
                                        #{exam.classStats.rank}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AccordionTab>
                ))}
            </Accordion>
        </div>
    );
};

export default ParentResultView;